import { useEffect, useRef, useState } from 'react'

import { toast } from 'react-toastify'

import {
  Banner,
  Button,
  Card,
  Dialog,
  EmptyState,
  Icon,
  Layout,
  Page,
  SpinnerWheel,
  Table,
  Tag,
  Text,
  Tooltip,
} from '@loadsmart/miranda-react'

import { useSession } from 'auth/ui/hooks/useSession'
import { AnalyticsEvent } from 'core/analytics/events'
import { useAnalytics } from 'core/analytics/useAnalytics'
import AppLayout from 'core/ui/components/AppLayout'
import Header from 'core/ui/components/Header'
import { type OpenDockOrgReport } from 'managed-analytics/domain/OpenDockOrgReport'
import { RequestType } from 'managed-analytics/domain/RequestType'
import PerformanceReportFeedbackDialog from 'managed-analytics/ui/components/PerformanceReportFeedbackDialog'
import { useOpenDockPdfReportsAccess } from 'managed-analytics/ui/hooks/useOpenDockPdfReportsAccess'
import { useRequestZendesk } from 'managed-analytics/ui/hooks/useRequestZendesk'
import { reportPdfFileName, triggerBlobDownload } from 'managed-analytics/ui/openDockReportPdf'
import {
  PreviewFrame,
  ReportThumbnail,
  RowActions,
  ThumbnailBar,
  ThumbnailChart,
  ThumbnailColumn,
  ThumbnailLine,
} from 'managed-analytics/ui/PerformanceReportsPage.styles'
import { getOpenDockReportPdf } from 'managed-analytics/use-cases/getOpenDockReports'

// Route performance-report feedback tickets to the same Zendesk queue as the
// "Try it now" flow instead of the default report-feedback tag.
const FEEDBACK_TAGS = ['engineering__data_engineering__managed_analytics']

function formatReportDate(value: string) {
  const [year, month, day] = value.split('-').map(Number)
  if (!year || !month || !day) return value

  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

// uploadedAt is an ISO timestamp, unlike reportDate's plain YYYY-MM-DD.
function formatUploadedAt(value: string) {
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return ''

  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function reportTitle(report: OpenDockOrgReport) {
  return `Performance report — ${formatReportDate(report.reportDate)}`
}

type ReportPreview = {
  uuid: string
  url: string
  title: string
}

export default function PerformanceReportsPage() {
  const session = useSession()
  const { track } = useAnalytics()
  const { isNavVisible, isLoading, reports, isError, isForbidden } = useOpenDockPdfReportsAccess()
  const { requestZendesk, isSendingRequest, hasSuccess, hasError } = useRequestZendesk()
  const [pendingAction, setPendingAction] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [preview, setPreview] = useState<ReportPreview | null>(null)
  const [feedbackReport, setFeedbackReport] = useState<OpenDockOrgReport | null>(null)
  // Guards against an earlier "open" resolving after a later one and
  // overwriting the preview the user actually asked for.
  const previewRequestRef = useRef(0)

  const latest = reports[0]
  const earlier = reports.slice(1)

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview.url)
    }
  }, [preview])

  useEffect(() => {
    if (!hasSuccess) return
    toast('Thanks! Your feedback has been sent.', { type: 'success' })
    setFeedbackReport(null)
  }, [hasSuccess])

  useEffect(() => {
    if (!hasError) return
    toast('Your feedback was not sent. Please try again in a moment.', { type: 'error' })
  }, [hasError])

  function closePreview() {
    previewRequestRef.current += 1
    setPreview(current => {
      if (current) URL.revokeObjectURL(current.url)
      return null
    })
  }

  if (isLoading) {
    return (
      <AppLayout>
        <Header
          title={
            <Text color="color-text-primary" variant="body-md-bold">
              Performance Reports
            </Text>
          }
        />
        <Page.Content>
          <Page.Body>
            <Layout.Group align="center" justify="center" style={{ minHeight: '40vh' }}>
              <SpinnerWheel size="32px" />
            </Layout.Group>
          </Page.Body>
        </Page.Content>
      </AppLayout>
    )
  }

  if (!isNavVisible) {
    return (
      <AppLayout>
        <Header
          title={
            <Text color="color-text-primary" variant="body-md-bold">
              Performance Reports
            </Text>
          }
        />
        <Page.Content>
          <Page.Body>
            {isError ? (
              <Banner variant="danger">
                <Banner.Title>Reports could not be loaded</Banner.Title>
                <Banner.Description>
                  Refresh the page to try again. If it keeps happening, let us know.
                </Banner.Description>
              </Banner>
            ) : (
              <Text variant="body-md" color="color-text-secondary">
                {isForbidden
                  ? 'Your organization is not enabled for performance reports.'
                  : 'Performance reports are not available for this account.'}
              </Text>
            )}
          </Page.Body>
        </Page.Content>
      </AppLayout>
    )
  }

  async function handleOpen(report: OpenDockOrgReport, action: 'open' | 'download') {
    setError('')
    setPendingAction(`${action}-${report.uuid}`)
    const requestId = action === 'open' ? (previewRequestRef.current += 1) : 0

    try {
      const pdf = await getOpenDockReportPdf(report.uuid)
      track(
        action === 'open'
          ? AnalyticsEvent.PerformanceReportViewPdf
          : AnalyticsEvent.PerformanceReportDownloadPdf,
        {
          org_id: report.orgId,
          warehouse_id: '_org',
          report_date: report.reportDate,
          caller_role: session?.user.issuer,
        }
      )

      if (action === 'download') {
        triggerBlobDownload(pdf, reportPdfFileName(report.reportDate))
        return
      }

      // A newer open request started while this one was in flight — drop it.
      if (requestId !== previewRequestRef.current) return

      setPreview(current => {
        if (current) URL.revokeObjectURL(current.url)
        return {
          uuid: report.uuid,
          url: URL.createObjectURL(pdf),
          title: reportTitle(report),
        }
      })
    } catch {
      setError(
        action === 'download'
          ? 'Unable to download this report. Try again in a moment.'
          : 'Unable to open this report. Try again in a moment.'
      )
    } finally {
      setPendingAction(current => (current === `${action}-${report.uuid}` ? null : current))
    }
  }

  function openFeedback(report: OpenDockOrgReport) {
    setFeedbackReport(report)
  }

  // Close the preview first so the feedback dialog isn't stacked on top of
  // another modal. The dialog names the report, so the context survives.
  function openFeedbackForPreview() {
    const report = reports.find(item => item.uuid === preview?.uuid)
    if (!report) return

    closePreview()
    setFeedbackReport(report)
  }

  function handleSendFeedback(description: string) {
    if (!feedbackReport) return

    track(AnalyticsEvent.PerformanceReportFeedback, {
      org_id: feedbackReport.orgId,
      warehouse_id: '_org',
      report_date: feedbackReport.reportDate,
      caller_role: session?.user.issuer,
    })

    const reportName = `Performance Report — ${formatReportDate(feedbackReport.reportDate)}`
    requestZendesk(RequestType.NewRequest, reportName, description, undefined, FEEDBACK_TAGS)
  }

  return (
    <AppLayout>
      <Header
        title={
          <Text color="color-text-primary" variant="body-md-bold">
            Performance Reports
          </Text>
        }
      />
      <Page.Content>
        <Page.Body>
          <Layout.Stack gap="spacing-6">
            <Text variant="body-md" color="color-text-secondary">
              Warehouse performance reports for your organization.
            </Text>

            {error && (
              <Banner variant="danger" dismissible onDismiss={() => setError('')}>
                <Banner.Description>{error}</Banner.Description>
              </Banner>
            )}

            {!latest && (
              <EmptyState variant="page">
                <EmptyState.Header>No reports yet</EmptyState.Header>
                <EmptyState.Message>
                  Your first performance report will appear here as soon as it is published.
                </EmptyState.Message>
              </EmptyState>
            )}

            {latest && (
              <Layout.Stack gap="spacing-6">
                <Card>
                  <Card.Body>
                    {/*
                      The thumbnail sits beside the whole block — heading
                      included — so the two columns are of a similar height
                      and their tops line up.
                    */}
                    <Layout.Group gap="spacing-5" align="flex-start">
                      <ReportThumbnail aria-hidden="true">
                        <ThumbnailBar />
                        <ThumbnailLine $width="88%" />
                        <ThumbnailLine $width="72%" />
                        <ThumbnailChart>
                          <ThumbnailColumn $height="40%" />
                          <ThumbnailColumn $height="66%" />
                          <ThumbnailColumn $height="52%" />
                          <ThumbnailColumn $height="88%" />
                          <ThumbnailColumn $height="71%" />
                        </ThumbnailChart>
                        <ThumbnailLine $width="80%" />
                        <ThumbnailLine $width="57%" />
                      </ReportThumbnail>

                      <Layout.Stack gap="spacing-3">
                        <Layout.Group align="center" gap="spacing-2">
                          <Text variant="heading-sm-bold" color="color-text-primary">
                            {formatReportDate(latest.reportDate)}
                          </Text>
                          <Tag variant="success" size="small">
                            Latest
                          </Tag>
                        </Layout.Group>

                        {formatUploadedAt(latest.uploadedAt) && (
                          <Text variant="body-sm" color="color-text-tertiary">
                            Published {formatUploadedAt(latest.uploadedAt)}
                          </Text>
                        )}

                        <Layout.Group gap="spacing-2">
                          <Button
                            variant="primary"
                            loading={pendingAction === `open-${latest.uuid}`}
                            disabled={pendingAction === `open-${latest.uuid}`}
                            onClick={() => handleOpen(latest, 'open')}
                          >
                            Open report
                          </Button>
                          <Button
                            variant="secondary"
                            leading={<Icon name="download" />}
                            loading={pendingAction === `download-${latest.uuid}`}
                            disabled={pendingAction === `download-${latest.uuid}`}
                            onClick={() => handleOpen(latest, 'download')}
                          >
                            Download PDF
                          </Button>
                          <Button
                            variant="secondary"
                            leading={<Icon name="send" />}
                            onClick={() => openFeedback(latest)}
                          >
                            Feedback
                          </Button>
                        </Layout.Group>
                      </Layout.Stack>
                    </Layout.Group>
                  </Card.Body>
                </Card>

                {earlier.length > 0 && (
                  <Layout.Stack gap="spacing-3">
                    <Text variant="body-md-bold" color="color-text-primary">
                      Earlier reports
                    </Text>
                    <Table>
                      <Table.Head>
                        <Table.Row>
                          <Table.HeadCell columnId="reportDate">Report date</Table.HeadCell>
                          <Table.HeadCell columnId="publishedAt">Published</Table.HeadCell>
                          <Table.HeadCell columnId="actions" alignment="right">
                            Actions
                          </Table.HeadCell>
                        </Table.Row>
                      </Table.Head>
                      <Table.Body>
                        {earlier.map(report => (
                          <Table.Row key={report.uuid}>
                            <Table.Cell>{formatReportDate(report.reportDate)}</Table.Cell>
                            <Table.Cell>
                              <Text variant="body-sm" color="color-text-tertiary">
                                {formatUploadedAt(report.uploadedAt) || '—'}
                              </Text>
                            </Table.Cell>
                            <Table.Cell alignment="right">
                              <RowActions>
                                <Button
                                  variant="tertiary"
                                  loading={pendingAction === `open-${report.uuid}`}
                                  disabled={pendingAction === `open-${report.uuid}`}
                                  onClick={() => handleOpen(report, 'open')}
                                >
                                  Open
                                </Button>
                                <Tooltip message="Download PDF" placement="top">
                                  <Button
                                    variant="tertiary"
                                    aria-label={`Download PDF for ${formatReportDate(report.reportDate)}`}
                                    leading={<Icon name="download" />}
                                    loading={pendingAction === `download-${report.uuid}`}
                                    disabled={pendingAction === `download-${report.uuid}`}
                                    onClick={() => handleOpen(report, 'download')}
                                  />
                                </Tooltip>
                                <Tooltip message="Feedback" placement="top">
                                  <Button
                                    variant="tertiary"
                                    aria-label={`Feedback about ${formatReportDate(report.reportDate)}`}
                                    leading={<Icon name="send" />}
                                    onClick={() => openFeedback(report)}
                                  />
                                </Tooltip>
                              </RowActions>
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  </Layout.Stack>
                )}
              </Layout.Stack>
            )}
          </Layout.Stack>
        </Page.Body>
      </Page.Content>

      <Dialog open={preview !== null} size="full-page" variant="neutral" onClose={closePreview}>
        <Dialog.Header>
          {preview?.title}
          <Dialog.Close />
        </Dialog.Header>
        <Dialog.Body>
          {preview && <PreviewFrame title={preview.title} src={preview.url} />}
        </Dialog.Body>
        <Dialog.Actions>
          <Dialog.ActionTertiary leading={<Icon name="send" />} onClick={openFeedbackForPreview}>
            Feedback
          </Dialog.ActionTertiary>
          <Dialog.ActionPrimary onClick={closePreview}>Close</Dialog.ActionPrimary>
        </Dialog.Actions>
      </Dialog>

      <PerformanceReportFeedbackDialog
        open={feedbackReport !== null}
        reportLabel={
          feedbackReport
            ? `Performance Report — ${formatReportDate(feedbackReport.reportDate)}`
            : ''
        }
        isSending={isSendingRequest}
        onCancel={() => setFeedbackReport(null)}
        onSend={handleSendFeedback}
      />
    </AppLayout>
  )
}
